import { collapseDecorationsPluginKey } from "./buildCollapseRunDecorations";

import type { EditorState } from "@tiptap/pm/state";

/**
 * Meta flag on a transaction telling the collapse selection guard to leave its selection alone.
 * Used when we deliberately park a selection across hidden content (e.g. to cut a collapsed section
 * whole) and don't want the guard to shrink it back to a `NodeSelection`.
 */
export const BYPASS_COLLAPSE_SELECTION_GUARD = "collapse:bypassSelectionGuard";

/**
 * Grow a copy/cut range so it includes a collapsed section's hidden content when the range stops at
 * the section's visible head — a collapsed heading whose whole title is selected, or a collapsed
 * horizontal rule.
 */
export function collapsedCopyRange(
  state: EditorState,
  from: number,
  to: number,
): { from: number; to: number } {
  if (from === to) {
    return { from, to };
  }

  const sections = collapseDecorationsPluginKey.getState(state)?.sections;

  if (!sections) {
    return { from, to };
  }

  const $to = state.doc.resolve(to);

  if ($to.depth > 0) {
    if (to === $to.end(1) && from <= $to.start(1)) {
      const section = sections.get($to.before(1));

      if (section) {
        return { from, to: section.end };
      }
    }

    return { from, to };
  }

  const head = $to.nodeBefore;

  if (head && from <= to - head.nodeSize) {
    const section = sections.get(to - head.nodeSize);

    if (section) {
      return { from, to: section.end };
    }
  }

  return { from, to };
}
